import React from 'react';
import { graphql, Link } from 'gatsby';
import Helmet from 'react-helmet';
import classnames from 'classnames';

import AnchorLink from '../components/AnchorLink';
import { logos } from '../components/Bio';
import IconLink from '../components/IconLink';

import * as skills from '../data/skills';
import * as experiences from '../data/experience';

import portrait from '../images/resume-pic.jpg';
import frFlag from '../images/flags/fr.svg';
import gbFlag from '../images/flags/gb.svg';


const description = `\
Passionate, highly autonomous engineer with a love for exploration, \
attention to detail and a taste for sharing knowledge. \
Fervent about crafting software tools that empower humans, \
insistent they should be open, solid and dependable.`;

const translation = (name, url, flag) => (
    <Link to={url} className="translation">
        <img src={flag} className="flag" alt={name}/>
    </Link>
);


export default class ResumePage extends React.Component {
    render() {
        const siteMetadata = this.props.data.site.siteMetadata;
        const author = siteMetadata.author;
        const { key } = this.props.pageContext;
        const { resume, education, interests } = this.props.data;

        return (
            <>
                <Helmet title={`Résumé | ${siteMetadata.title}`}>
                    <html lang="en"/>
                    <link rel="canonical" href={`${siteMetadata.siteUrl}resume`} />
                    <meta name="description" value={`Julien Hartmann's résumé`} />
                    <meta name="author" value="Julien Hartmann" />
                    <meta property="og:type" content="website" />
                    <meta property="og:title" content="Julien Hartmann's résumé" />
                    <meta property="og:site_name" content={siteMetadata.title} />
                    <meta property="og:description" content={description} />
                    <meta property="og:image" content={portrait} />
                    <meta property="twitter:site:id" content={siteMetadata.twitterId} />
                    <link rel="alternate" type="application/json+oembed" href="/oembed.json" />
                </Helmet>
                <main className="resume-page"><article className="resume">
                    { resume.frlink && <nav className="translations">
                        {translation('English', `resume/${key}`, gbFlag)}
                        {translation('French', resume.frlink, frFlag)}
                    </nav> }
                    { resume.title && <h1>{resume.title}</h1> }
                    <div className="content">
                    <p>Expert at identifying problems in varied
                        environments and architecting robust solutions:</p>
                    <div className="side-by-side abstract">
                        <p className="side">
                            Pas­sion­ate, <b>high­ly au­tonomous engineer</b> with a love for ex­plo­ration,
                            at­ten­tion to de­tail and a taste for shar­ing knowl­edge.
                            Fer­vent about craft­ing soft­ware tools that <strong>em­pow­er
                            hu­mans</strong>, in­sis­tent they should be open, sol­id and de­pend­able.</p>
                        <p className="side">
                            Con­firmed <b>IT in­­fras­truc­­ture ar­chi­­tect</b> with 8 years of ex­pe­ri­ence
                            de­sign­ing, plan­n­ing, mi­­grat­ing and eval­u­at­ing large-scale
                            en­vi­ron­­ments, with a fo­cus on <strong>cross-cut­ting con­cerns</strong>,
                            em­pha­­siz­ing a <strong>sys­temic ap­proach</strong> cen­tered
                            on <strong>us­er needs</strong>.</p>
                    </div>
                    { resume.variant == 'developer' &&
                        <p>Looking to combine his skills at analyzing and
                        structuring complex systems to build professional, user-focused
                        software solutions.</p> }

                    <section className="skills">
                        <h2><span>Key skills</span></h2>
                        {skills[resume.variant]()}
                    </section>
                    <section className="experience">
                        <h2><span>Experience</span></h2>
                        {resume.experiences.map((key) => experiences[key](Experience))}
                    </section>
                    <section className="education">
                        <h2><span>Education</span></h2>
                        <span dangerouslySetInnerHTML={{ __html: education.html }} />
                    </section>
                    <section className="interests">
                        <h2><span>Interests</span></h2>
                        <span dangerouslySetInnerHTML={{ __html: interests.html }} />
                    </section>
                    </div>
                </article>
                <AnchorLink className="back-to-top" href="#">back to top</AnchorLink>
                </main>
                <aside className="resume-aside"><div className="sticky">
                    <section>
                        <img className="profile-picture" src={portrait} alt="Julien Hartmann" />
                        <div className="profile-name">Julien Hartmann</div>
                        { resume.contact && <Contact value={resume.contact} /> }
                        <ul className="s-links">{author.links.map(({name, url, title}, index) => (
                            <li key={index}><IconLink href={url} text={title} src={logos[name]} /></li>
                        ))}</ul>
                    </section>
                </div></aside>
            </>
        );
    }
}

const Contact = ({className, value}) => {
    const { phone, mail, location } = value;
    const phoneHref = phone && `tel:+${phone.replace(/\D/g, '')}`;
    return (<ul className={classnames('contact', className)}>
        { phone && <li className="phone"><a className="phone-link" href={phoneHref}>{phone}</a></li> }
        { mail && <li className="mail">{mail}</li> }
        { location && <li className="location">{location}</li> }
    </ul>);
};

const Experience = ({children, company, id, title}) => (
    <section className="resume-experience" id={id}>
        <header>
            <h3>{title}</h3>
            {company && <div className="company">
                {company.link ?
                    <a href={company.link} rel="external noopener noreferrer">
                        {company.name}
                        {company.logo && <img className="logo" src={company.logo} alt=""/> }
                    </a>
                : <> {company.name} </>}
            </div>}
        </header>
        <main>{children}</main>
    </section>
);

export const pageQuery = graphql`
query ResumeByKey($key: String!) {
    resume: resumeYaml(key: { eq: $key }) {
        title
        variant
        frlink
        contact { mail location }
        experiences
    }
    site {
        siteMetadata {
            title
            siteUrl
            twitterId
            author {
                links { url name title }
            }
        }
    }
    education: markdownRemark(frontmatter: {chunk: {eq: "resume-education"}}) { html }
    interests: markdownRemark(frontmatter: {chunk: {eq: "resume-interests"}}) { html }
}
`
